import mapboxgl from 'mapbox-gl';
import { clipRingToHorizon, createHorizonClipper, GlobeCoordinate, HorizonClipper } from './horizon-clipping';

// PoC: renders polar (>=85 deg latitude) hexagons on a 2D canvas positioned
// over the map, projecting each vertex with `map.project()`. Unlike
// CustomLayerInterface, `map.project()` goes through Mapbox's globe ECEF
// transform (the same path Marker/Popup use), so it follows the sphere
// correctly instead of rendering on a flat Mercator plane.
//
// Input rings are already densified along great-circle arcs by utility.ts.
interface ProjectedFeature {
    feature: any;
    points: mapboxgl.Point[];
}

export class PolarHexagonCanvasOverlay {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private features: any[] = [];
    private colorScale: ((value: number) => string) | null = null;
    private renderHandler = () => this.draw();
    // Screen-space polygons from the most recent draw(), reused for hit
    // testing (mousemove/click) instead of re-projecting on every event.
    private projectedFeatures: ProjectedFeature[] = [];

    constructor(private map: mapboxgl.Map) {
        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.pointerEvents = 'none';
        this.map.getCanvasContainer().appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        this.map.on('render', this.renderHandler);
    }

    setData(featureCollection: any) {
        this.features = featureCollection?.features || [];
        this.map.triggerRepaint();
    }

    setColorScale(colorScale: (value: number) => string) {
        this.colorScale = colorScale;
        this.map.triggerRepaint();
    }

    remove() {
        this.map.off('render', this.renderHandler);
        this.canvas.remove();
    }

    // Returns the topmost polar feature under a screen point (e.g. `e.point`
    // from a Mapbox mouse event), or null. Canvas overlays aren't Mapbox
    // layers, so they don't participate in `queryRenderedFeatures`/layer-
    // scoped mouse events; callers must hit-test manually.
    featureAtPoint(point: { x: number; y: number }): any | null {
        for (let i = this.projectedFeatures.length - 1; i >= 0; i--) {
            const { feature, points } = this.projectedFeatures[i];
            if (isPointInPolygon(point, points)) {
                return feature;
            }
        }

        return null;
    }

    private draw() {
        this.resizeCanvasIfNeeded();
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.projectedFeatures = [];

        const clipper = this.getHorizonClipper();
        if (!clipper) {
            return;
        }

        this.features.forEach((feature) => this.drawFeature(feature, clipper));
    }

    private drawFeature(feature: any, clipper: HorizonClipper) {
        const value = feature.properties?.value?.data;
        if (value === 0 || value === null || value === undefined) {
            return;
        }

        const rings = feature.geometry?.type === 'Polygon' ? feature.geometry.coordinates : [];
        const outerRing = rings[0] || [];
        if (outerRing.length < 3) {
            return;
        }

        const visibleRings = clipRingToHorizon(outerRing as GlobeCoordinate[], clipper);
        if (!visibleRings.length) {
            return;
        }

        this.ctx.fillStyle = this.colorScale ? this.colorScale(value) : '#1a8cff';
        visibleRings.forEach((ring) => {
            const points = ring.map(([longitude, latitude]) => this.map.project([longitude, latitude]));
            if (points.length < 4 || points.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))) {
                return;
            }

            this.projectedFeatures.push({ feature, points });
            this.ctx.beginPath();
            points.forEach((point, index) => {
                if (index === 0) {
                    this.ctx.moveTo(point.x, point.y);
                } else {
                    this.ctx.lineTo(point.x, point.y);
                }
            });
            this.ctx.closePath();
            this.ctx.fill();
        });
    }

    private getHorizonClipper() {
        // The canvas is outside Mapbox's depth buffer, so the public Free Camera
        // position is the only camera input used to reconstruct horizon occlusion.
        // This has been verified with Mapbox GL JS 2.14.1 in Globe mode; re-check
        // it when upgrading Mapbox rather than switching to private transforms.
        const position = this.map.getFreeCameraOptions().position;
        if (!position) {
            return null;
        }

        const cameraLngLat = position.toLngLat();
        return createHorizonClipper([cameraLngLat.lng, cameraLngLat.lat], position.toAltitude());
    }

    private resizeCanvasIfNeeded() {
        const mapCanvas = this.map.getCanvas();
        const width = mapCanvas.clientWidth;
        const height = mapCanvas.clientHeight;
        const dpr = window.devicePixelRatio || 1;
        const targetWidth = Math.round(width * dpr);
        const targetHeight = Math.round(height * dpr);

        if (this.canvas.width === targetWidth && this.canvas.height === targetHeight) {
            return;
        }

        this.canvas.width = targetWidth;
        this.canvas.height = targetHeight;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
}

function isPointInPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): boolean {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const vertex of polygon) {
        minX = Math.min(minX, vertex.x);
        maxX = Math.max(maxX, vertex.x);
        minY = Math.min(minY, vertex.y);
        maxY = Math.max(maxY, vertex.y);
    }
    if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
        return false;
    }

    // Ray casting (even-odd rule).
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        const crosses = a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
        if (crosses) {
            inside = !inside;
        }
    }

    return inside;
}
